import React, { useState } from "react";
import { Button, Modal, Form, Table, Dropdown } from "react-bootstrap";
import { PlusSquare } from "react-bootstrap-icons";
import BuyerWarehouseApi from "../../api/BuyerWarehouseApi";
import BuyerApi from "../../api/BuyerApi";
import AuthorizedPage from "../../component/AuthorizedPage";
import QuickSearchBox from "../../component/QuickSearchBox";
import { Role } from "../../domain/Role";
import GoogleMapPicker from "../../component/GoogleMapPicker";
import { OZISIK_WAREHOUSE_LOCATION } from '../../Constants';

interface Buyer {
  id: number;
  name: string;
  surname: string;
  title: string;
}

interface Warehouse {
  id?: number;
  buyerId?: number;
  code: string;
  logoCode: string;
  title: string;
  buyerWarehouseId: number;
  longitude: number;
  latitude: number;
  buyerTitle?: string;
}

// --- GÜNCELLENMİŞ ARAMALI DROPDOWN ---
// includeAllOption: Filtrelerde 'Tümü' olsun ama Modal içinde olmasın diye eklendi.
const CustomSearchableDropdown = ({
    items,
    selectedValue,
    onSelect,
    placeholder,
    labelKey = "title",
    includeAllOption = true
}: any) => {
    const [searchTerm, setSearchTerm] = useState("");

    // Seçili öğeyi bul
    const selectedItem = items.find((i: any) => i.id == selectedValue);

    // Listeyi filtrele
    const filteredItems = items.filter((item: any) =>
        item[labelKey].toString().toLocaleLowerCase('tr-TR').includes(searchTerm.toLocaleLowerCase('tr-TR'))
    );

    return (
        <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start d-flex justify-content-between align-items-center bg-white">
                <span className="text-truncate">
                    {selectedItem ? selectedItem[labelKey] : (includeAllOption ? "Tüm Müşteriler" : "Seçiniz")}
                </span>
            </Dropdown.Toggle>

            <Dropdown.Menu className="w-100 p-2 shadow" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <Form.Control
                    autoFocus
                    placeholder={`${placeholder} Ara...`}
                    className="mb-2 sticky-top"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm}
                />

                {includeAllOption && (
                    <>
                        <Dropdown.Item onClick={() => onSelect("Tümü")} active={!selectedValue || selectedValue === "Tümü"}>
                            Tüm Müşteriler
                        </Dropdown.Item>
                        <Dropdown.Divider />
                    </>
                )}

                {filteredItems.length > 0 ? (
                    filteredItems.map((item: any) => (
                        <Dropdown.Item
                            key={item.id}
                            onClick={() => onSelect(item.id)}
                            active={selectedValue == item.id}
                        >
                            {item[labelKey]}
                        </Dropdown.Item>
                    ))
                ) : (
                    <div className="text-muted text-center small p-2">Sonuç bulunamadı</div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

class BuyerWarehouseManagement extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      warehousesRaw: [],
      warehouses: [],
      buyers: [],

      // Filtreleme State'i
      selectedBuyerId: "",

      // Modal State'i
      selectedWarehouse: {} as Warehouse,
      showModal: false,
      modalSelectedBuyerId: null, // 🔥 Modal içindeki dropdown için yeni state

      // Harita State'i
      selectedLat: OZISIK_WAREHOUSE_LOCATION.latitude,
      selectedLng: OZISIK_WAREHOUSE_LOCATION.longitude,
    };
  }

  componentDidMount() {
    this.loadWarehouses();
    this.loadBuyers();
  }

  async loadWarehouses() {
    try {
      const response = await BuyerWarehouseApi.listAllBuyerWarehouses();
      if (response.success) {
        this.setState({ warehouses: response.data, warehousesRaw: response.data });
      } else {
        alert("Depolar alınamadı.");
      }
    } catch {
      alert("Depolar alınırken hata oluştu.");
    }
  }

  async loadBuyers() {
    const response = await BuyerApi.listAll();
    if (response.success) {
      this.setState({ buyers: response.data });
    }
  }

  openModal = (warehouse?: Warehouse) => {
    if (warehouse && warehouse.latitude && warehouse.longitude) {
      this.setState({
        showModal: true,
        selectedWarehouse: warehouse,
        modalSelectedBuyerId: warehouse.buyerId, // 🔥 Düzenlemede mevcut müşteriyi seç
        selectedLat: warehouse.latitude,
        selectedLng: warehouse.longitude,
      });
    } else {
      this.setState({
        showModal: true,
        selectedWarehouse: {} as Warehouse,
        modalSelectedBuyerId: null, // 🔥 Yeni kayıtta boş olsun
        selectedLat: OZISIK_WAREHOUSE_LOCATION.latitude,
        selectedLng: OZISIK_WAREHOUSE_LOCATION.longitude,
      });
    }
  };

  closeModal = () => {
    this.setState({ showModal: false, selectedWarehouse: {} as Warehouse, modalSelectedBuyerId: null });
  };

  onLocationSelect = (lat: number, lng: number) => {
    this.setState({ selectedLat: lat, selectedLng: lng });
  };

  // Filtre Dropdown Handler
  onBuyerSelect = async (val: any) => {
    const buyerId = val === "Tümü" ? "" : val;
    this.setState({ selectedBuyerId: buyerId });

    if (buyerId) {
      const response = await BuyerWarehouseApi.listBuyerWarehouses(buyerId);
      if (response.success) {
        this.setState({ warehouses: response.data, warehousesRaw: response.data });
      }
    } else {
      this.loadWarehouses();
    }
  };


  async onSubmit(e: any) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formObject: any = Object.fromEntries(formData);

    // Dropdown'dan veri seçilmediyse uyarı ver
    if (!formObject.buyerId) {
        alert("Lütfen bir müşteri seçiniz.");
        return;
    }

    if (!formObject.id || formObject.id === "") {
      delete formObject.id;
    } else {
      formObject.id = Number(formObject.id);
    }

    formObject.buyerId = Number(formObject.buyerId);
    formObject.latitude = this.state.selectedLat;
    formObject.longitude = this.state.selectedLng;

    const selectedBuyer = this.state.buyers.find((b: Buyer) => b.id === formObject.buyerId);
    if (selectedBuyer) {
      formObject.buyerTitle = selectedBuyer.title;
    }

    const response = await BuyerWarehouseApi.createOrUpdateWarehouse(formObject);

    if (response.success) {
      this.closeModal();
      this.loadWarehouses();
    } else {
      alert("Hata oluştu: " + (response?.error?.message ?? "Bilinmeyen hata"));
    }
  }

  renderModal() {
    const { selectedWarehouse, buyers, selectedLat, selectedLng, modalSelectedBuyerId } = this.state;
    const { id, code, logoCode, title } = selectedWarehouse;

    return (
      <Modal show={this.state.showModal} onHide={this.closeModal} backdrop="static" size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold">Depo Tanımı</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">

          <div className="border rounded-3 shadow-sm p-4 bg-white">
            <Form onSubmit={(e) => this.onSubmit(e)}>
              {id && <Form.Control type="hidden" name="id" defaultValue={id} />}

              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-semibold">Müşteri</Form.Label>

                  {/* 🔥 MODAL İÇİNDE ARAMALI DROPDOWN 🔥 */}
                  <CustomSearchableDropdown
                    items={buyers}
                    selectedValue={modalSelectedBuyerId}
                    onSelect={(val: any) => this.setState({ modalSelectedBuyerId: val })}
                    placeholder="Müşteri"
                    labelKey="title"
                    includeAllOption={false} // Modalda 'Tümü' seçeneği olmasın
                  />

                  {/* 🔥 ÖNEMLİ: Form Submit edilirken bu hidden input sayesinde veri gider 🔥 */}
                  <input type="hidden" name="buyerId" value={modalSelectedBuyerId || ""} />

                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-semibold">Kodu</Form.Label>
                  <Form.Control name="code" defaultValue={code || ""} required />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-semibold">Logo Kodu</Form.Label>
                  <Form.Control name="logoCode" defaultValue={logoCode || ""} required />
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-semibold">Başlık</Form.Label>
                  <Form.Control name="title" defaultValue={title || ""} required />
                </div>
              </div>

              <div className="mt-4">
                <Form.Label className="fw-semibold">Konum Seçimi</Form.Label>
                <div className="rounded border shadow-sm overflow-hidden">
                  <GoogleMapPicker
                    latitude={selectedLat}
                    longitude={selectedLng}
                    onSelectLocation={this.onLocationSelect}
                  />
                </div>
                <div className="text-muted small mt-2">
                  Harita üzerinden depo konumunu seçiniz.
                </div>
              </div>

              <Form.Control type="hidden" name="latitude" value={selectedLat} />
              <Form.Control type="hidden" name="longitude" value={selectedLng} />

              <div className="d-flex justify-content-end mt-4">
                <Button variant="primary" type="submit" className="px-4">
                  Kaydet
                </Button>
              </div>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    );
  }


  render() {
    const { warehouses, warehousesRaw, buyers, selectedBuyerId } = this.state;

    return (
      <AuthorizedPage role={Role.BACKOFFICE_ADMIN}>
        {this.renderModal()}

        <div className="container mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">

            <h3 className="fw-bold text-secondary m-0">Müşteri Deposu Yönetimi</h3>

            <Button
              variant="success"
              className="d-flex align-items-center gap-2 shadow-sm"
              onClick={() => this.openModal()}
            >
              <PlusSquare size={20} />
              Yeni Depo
            </Button>
          </div>

          <div className="d-flex align-items-center gap-3 mb-3">
            <QuickSearchBox
              items={warehousesRaw}
              onSearch={(filtered: Warehouse[]) => this.setState({ warehouses: filtered })}
            />
            <div style={{ width: "300px", flexShrink: 0 }}>
              <CustomSearchableDropdown
                items={buyers}
                selectedValue={selectedBuyerId}
                onSelect={this.onBuyerSelect}
                placeholder="Müşteri"
                labelKey="title"
                includeAllOption={true}
              />
            </div>
          </div>

          <Table bordered hover responsive className="shadow-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Müşteri</th>
                <th>Başlık</th>
                <th>Longitude</th>
                <th>Latitude</th>
                <th style={{ width: "120px" }}>İşlem</th>
              </tr>
            </thead>

            <tbody>
              {warehouses.map((w: Warehouse) => (
                <tr key={w.id} className="table-row-hover">
                  <td>{w.buyerTitle}</td>
                  <td>{w.title}</td>
                  <td>{w.longitude}</td>
                  <td>{w.latitude}</td>
                  <td>
                    <Button variant="warning" size="sm" className="w-100" onClick={() => this.openModal(w)}>
                      Düzenle
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </AuthorizedPage>
    );
  }

}

export default BuyerWarehouseManagement;